/**
 * Guests Feature Hooks
 * React Query hooks for Guests data fetching
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/useAuth';
import {
  getOspiti,
  getOspiteById,
  createOspite,
  updateOspite,
  deleteOspite,
  getOspitiStats,
} from '@/api/queries';
import type { Ospite, OspiteInsert, OspiteUpdate } from '@/types';

// Query keys
export const guestsKeys = {
  all: ['guests'] as const,
  lists: () => [...guestsKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...guestsKeys.lists(), filters] as const,
  details: () => [...guestsKeys.all, 'detail'] as const,
  detail: (id: string) => [...guestsKeys.details(), id] as const,
  stats: () => [...guestsKeys.all, 'stats'] as const,
};

/**
 * Hook to fetch all guests
 */
export function useGuests(search?: string) {
  const { currentUser } = useAuth();
  
  return useQuery({
    queryKey: guestsKeys.list({ search: search || '' }),
    queryFn: async () => {
      if (!currentUser?.id) throw new Error('User not authenticated');
      const { data, error } = await getOspiti({
        userId: currentUser.id,
        filters: search ? { search } : {},
        order: { column: 'cognome', ascending: true },
      });
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentUser?.id,
  });
}

/**
 * Hook to fetch a single guest by ID
 */
export function useGuest(id: string) {
  const { currentUser } = useAuth();
  
  return useQuery({
    queryKey: guestsKeys.detail(id),
    queryFn: async () => {
      if (!currentUser?.id || !id) throw new Error('Missing user or guest ID');
      const { data, error } = await getOspiteById(id, currentUser.id);
      if (error) throw error;
      return data;
    },
    enabled: !!currentUser?.id && !!id,
  });
}

/**
 * Hook to fetch guest stats
 */
export function useGuestStats() {
  const { currentUser } = useAuth();
  
  return useQuery({
    queryKey: guestsKeys.stats(),
    queryFn: async () => {
      if (!currentUser?.id) throw new Error('User not authenticated');
      const { count, error } = await getOspitiStats(currentUser.id);
      if (error) throw error;
      return { total: count || 0 };
    },
    enabled: !!currentUser?.id,
  });
}

/**
 * Hook to create a new guest
 */
export function useCreateGuest() {
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  
  return useMutation({
    mutationFn: async (guest: OspiteInsert) => {
      if (!currentUser?.id) throw new Error('User not authenticated');
      const { data, error } = await createOspite(guest, currentUser.id);
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: guestsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: guestsKeys.stats() });
    },
  });
}

/**
 * Hook to update a guest
 */
export function useUpdateGuest() {
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: OspiteUpdate }) => {
      if (!currentUser?.id) throw new Error('User not authenticated');
      const { data, error } = await updateOspite(id, updates, currentUser.id);
      if (error) throw error;
      return data;
    },
    onSuccess: (_: unknown, variables: { id: string }) => {
      queryClient.invalidateQueries({ queryKey: guestsKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: guestsKeys.lists() });
    },
  });
}

/**
 * Hook to delete a guest
 */
export function useDeleteGuest() {
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  
  return useMutation({
    mutationFn: async (id: string) => {
      if (!currentUser?.id) throw new Error('User not authenticated');
      const { error } = await deleteOspite(id, currentUser.id);
      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: guestsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: guestsKeys.stats() });
    },
  });
}
